/**
 * rev 17b addendum F3 — "ONE shared renderer for 409/503/422 in both dialogs." Both UnlinkOnlyDialog
 * and BulkForceDialog call `bulkForceAbortMessage()` for a thrown `poolBulkForce` error; neither forks
 * its own copy of this classification.
 *
 * These three statuses are REQUEST-LEVEL refusals — the server rejects the whole call before touching a
 * single email, so there is no `results` map to fall back on (unlike a 200 with per-email skips, the
 * normal case). 409 (stale sweep) gets dedicated copy because it is the single most likely refusal an
 * operator will hit in practice — the sweep pauses 02:00–08:00 daily, so a bulk action attempted in that
 * window 409s every time, and the raw server message alone doesn't tell the operator WHEN to retry. 503
 * (kill-switch off) and 422 (size/empty/unknown action) render the server's own message verbatim, the
 * same rule every other refusal in this module follows.
 *
 * F4's "may have already gone through" case is deliberately NOT handled here — a network failure or a
 * 5xx is not a recognizable request-level refusal (the server-guaranteed per-email commit means some
 * emails in the in-flight chunk may have landed even though the client never saw the response), and gets
 * its own distinct copy at the call site. `isPartialCrashError()` below is how a caller tells the two
 * apart without duplicating the classification.
 */
import { ApiRequestError } from "@/api/client";
import type { TKey } from "@/i18n/keys";

type BulkAbortClass = { kind: "stale_sweep" } | { kind: "server_message"; message: string } | null;

function classify(e: unknown): BulkAbortClass {
  if (!(e instanceof ApiRequestError)) return null;
  if (e.error.status === 409) return { kind: "stale_sweep" };
  if (e.error.status === 503 || e.error.status === 422) {
    return { kind: "server_message", message: e.error.message };
  }
  return null;
}

/** Renders a request-level bulk-force abort (409/503/422), or null when `e` isn't one — the caller then
 *  falls through to its own F4 ("may have already gone through") copy. */
export function bulkForceAbortMessage(e: unknown, t: (k: TKey) => string): string | null {
  const c = classify(e);
  if (!c) return null;
  return c.kind === "stale_sweep" ? t("pool.users.bulk.abort409") : c.message;
}

/** True for the network/5xx family F4 covers — anything that ISN'T a recognized 409/503/422 shape. */
export function isPartialCrashError(e: unknown): boolean {
  return classify(e) === null;
}
